import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import Select from '../../../components/widgets/Select';
import { Button } from '../../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import {
    ATTRIBUTION_TAB_IDS,
    TRANSLATE_PREFIX,
    getMacroAllowedPlatforms,
    isPostbackMacroDisallowedForCell,
    trackingLinkKeyFromMacro,
} from '../../consts';
import { useTabUpdater } from '../../formUpdaters';
import useConfirmDialog from '../../useConfirmDialog';
import { getPostbackEventTypeLabelKey } from '../../utils';
import OverwriteConfirmBody from './OverwriteConfirmBody';
import css from './CopyTemplatePanel.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_TEMPLATES_TAB`;
const PLATFORM_LABEL_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;
const TEMPLATES_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;
const OVERALL_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;
const COPY_TO_ALL_VALUE = '__all__';

const FILTER_CLASSIFICATION = Object.freeze({
    ALLOWED: 'ALLOWED',
    PLATFORM: 'PLATFORM',
    TEMPLATE: 'TEMPLATE',
});

/**
 * Filter source rows to those allowed on the target (template, event_type, platform) cell.
 * Mirrors `applyPostbackEventsPlatformsNext`'s new-combo seeding: drops macros forbidden by
 * `PLATFORM_TEMPLATE_DISALLOWED_MACROS_BY_EVENT_TYPE` (e.g. `{EVTNAME}` on HasOffers conversions)
 * AND macros not allowed on the target platform. Custom passthrough macros ({x?<network>}) are
 * platform-agnostic and preserved unless explicitly disallowed.
 * @param {Array<{singular_macro: string, partner_parameter_key: string}>} rows source template rows
 * @param {string} targetPlatform destination postback platform
 * @param {string} targetEventType destination event type
 * @param {?string} platformTemplate active platform template (e.g. 'hasoffers')
 * @returns {Array<{singular_macro: string, partner_parameter_key: string}>} rows allowed on the target cell
 */
export const filterRowsForTargetCell = (rows, targetPlatform, targetEventType, platformTemplate) =>
    (rows || []).filter(r => {
        if (isPostbackMacroDisallowedForCell(platformTemplate, targetEventType, r.singular_macro)) return false;
        if (trackingLinkKeyFromMacro(r.singular_macro)) return true;
        return getMacroAllowedPlatforms(r.singular_macro).includes(targetPlatform);
    });

// TEMPLATE = blocked by affiliate template for the event type (e.g. {EVTNAME} on HasOffers Conversions).
// PLATFORM = macro not in the target platform's allowed list (e.g. {IDFA} on web).
const classifyRowForTarget = (row, targetPlatform, targetEventType, platformTemplate) => {
    if (isPostbackMacroDisallowedForCell(platformTemplate, targetEventType, row.singular_macro)) {
        return FILTER_CLASSIFICATION.TEMPLATE;
    }
    if (trackingLinkKeyFromMacro(row.singular_macro)) return FILTER_CLASSIFICATION.ALLOWED;
    if (!getMacroAllowedPlatforms(row.singular_macro).includes(targetPlatform)) {
        return FILTER_CLASSIFICATION.PLATFORM;
    }
    return FILTER_CLASSIFICATION.ALLOWED;
};

const CopyTemplatePanel = ({ sourceEventType, sourcePlatform, readOnly = false, translate }) => {
    const { values } = useFormikContext();
    const updateTemplatesTab = useTabUpdater(TEMPLATES_TAB_ID);
    const { confirm, dialog } = useConfirmDialog();
    const [selectedTarget, setSelectedTarget] = useState(null);

    const overallTab = values.formData?.[OVERALL_TAB_ID] || {};
    const templates = values.formData?.[TEMPLATES_TAB_ID] || {};
    const platformTemplate = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const sourceRows = templates[sourceEventType]?.[sourcePlatform] || [];

    const targetOptions = useMemo(() => {
        const entries = overallTab.postback_events_and_platforms || [];
        const cells = entries
            .flatMap(entry => (entry.platforms || []).map(platform => ({ eventType: entry.event_type, platform })))
            .filter(c => !(c.eventType === sourceEventType && c.platform === sourcePlatform))
            .map(c => {
                const eventLabel = translate(getPostbackEventTypeLabelKey(c.eventType, c.platform));
                const platformLabel = translate(`${PLATFORM_LABEL_PREFIX}.${c.platform.toUpperCase()}`);
                return {
                    value: `${c.eventType}|${c.platform}`,
                    label: `${eventLabel} — ${platformLabel}`,
                    eventType: c.eventType,
                    platform: c.platform,
                };
            });
        if (cells.length > 1) {
            return [{ value: COPY_TO_ALL_VALUE, label: translate(`${TAB_PREFIX}.ALL_TEMPLATES`) }, ...cells];
        }
        return cells;
    }, [overallTab.postback_events_and_platforms, sourceEventType, sourcePlatform, translate]);

    // Clear a stale selection if its target cell disappeared from Overall Setup while open.
    useEffect(() => {
        if (selectedTarget && !targetOptions.some(opt => opt.value === selectedTarget.value)) {
            setSelectedTarget(null);
        }
    }, [targetOptions, selectedTarget]);

    if (readOnly || targetOptions.length === 0) return null;

    const performCopy = targets => {
        updateTemplatesTab(prevTab => {
            const next = { ...prevTab };
            targets.forEach(({ eventType, platform }) => {
                const eventBucket = { ...(next[eventType] || {}) };
                eventBucket[platform] = filterRowsForTargetCell(sourceRows, platform, eventType, platformTemplate);
                next[eventType] = eventBucket;
            });
            return next;
        });
        setSelectedTarget(null);
    };

    const buildFilterReport = targets =>
        targets
            .map(target => {
                const seen = new Set();
                const filtered = [];
                (sourceRows || []).forEach(row => {
                    if (!row.singular_macro || seen.has(row.singular_macro)) return;
                    const reason = classifyRowForTarget(row, target.platform, target.eventType, platformTemplate);
                    if (reason === FILTER_CLASSIFICATION.ALLOWED) return;
                    seen.add(row.singular_macro);
                    filtered.push({ macro: row.singular_macro, reason });
                });
                return { target, filtered };
            })
            .filter(group => group.filtered.length > 0);

    const handleApply = () => {
        if (!selectedTarget) return;
        const targets =
            selectedTarget.value === COPY_TO_ALL_VALUE
                ? targetOptions.filter(opt => opt.value !== COPY_TO_ALL_VALUE)
                : [selectedTarget];
        const report = buildFilterReport(targets);
        confirm({
            titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.OVERWRITE_TEMPLATE_TITLE`,
            body: <OverwriteConfirmBody report={report} translate={translate} />,
            onConfirm: () => performCopy(targets),
        });
    };

    return (
        <>
            <div className={css.copyPanel}>
                <div className={css.copyInfo}>
                    <Icon name="copy" size={IconSizes.SMALL} variant={IconVariants.DARK} interactive={false} />
                    <div className={css.copyText}>
                        <span className={css.copyTitle}>
                            <Translate id={`${TAB_PREFIX}.COPY_TEMPLATE_TITLE`} />
                        </span>
                        <span className={css.copySubtitle}>
                            <Translate id={`${TAB_PREFIX}.COPY_TEMPLATE_SUBTITLE`} />
                        </span>
                    </div>
                </div>
                <div className={css.copyActions}>
                    <Select
                        options={targetOptions}
                        value={selectedTarget}
                        onChange={setSelectedTarget}
                        placeholder={translate(`${TAB_PREFIX}.COPY_TEMPLATE_PLACEHOLDER`)}
                        isClearable
                        isSearchable
                        className={css.copySelect}
                        usePortal
                        menuPlacement="auto"
                    />
                    <Button
                        type="primary"
                        text={`${TAB_PREFIX}.COPY_TEMPLATE_BUTTON`}
                        onClick={handleApply}
                        disabled={!selectedTarget}
                    />
                </div>
            </div>
            {dialog}
        </>
    );
};

CopyTemplatePanel.propTypes = {
    sourceEventType: PropTypes.string.isRequired,
    sourcePlatform: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(CopyTemplatePanel);
